import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as fp from 'lodash/fp';
import { useEffect } from 'react';

import { useRecoilValue } from 'recoil';
import authAtom from '~/recoil/atom/auth';
import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';

import { IoLogInOutline } from 'react-icons/io5';

import { InputVariant } from '~/theme/components/Input';

import { useLocation } from 'react-router-dom';
import useLogin, { ConfirmStatus } from './hooks/useLogin';

type LoginModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

// TODO: error message 介面優化
export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const location = useLocation(); 
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const authUser = useRecoilValue(authAtom);
  const {
    isVerifying,
    confirmStatus,
    state: { form, error },
    handleChangeState,
    handleLogin,
  } = useLogin();

  const handleLineLogin = () => {
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID || '';
    const platformId = process.env.REACT_APP_PLATFORM_ID || '';
    const host = process.env.REACT_APP_BACKEND_URL || '';
    const callbackUrl =
      // @ts-ignore
      `${window.location.pathname}+${location.state?.previous}`;

    const redirectUri = `${host}httpLineLogin/redirect/${platformId}?callbackUrl=${callbackUrl}`;
    const u = new URL('https://access.line.me/oauth2/v2.1/authorize');
    u.searchParams.append('response_type', 'code');
    u.searchParams.append('client_id', clientId);
    u.searchParams.append('redirect_uri', redirectUri);
    u.searchParams.append('state', Math.random().toString(32).substring(2));
    u.searchParams.append('scope', 'profile openid');

    window.location.href = u.toString();
  };

  useEffect(() => {
    if (authUser) {
      onClose();
    }
  }, [authUser]);

  if (isLargerThan768) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="95%">
          <ModalCloseButton />
          <ModalHeader mt={6} p={0}>
            <HStack justifyContent="center" borderBottom="1px" borderColor="primary" py={2}>
              <Icon as={IoLogInOutline} boxSize="1.5rem" color="primary" />
              <Text fontSize="lg" fontWeight="normal">
                登入
              </Text>
            </HStack>
          </ModalHeader>

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="xs">
                手機登入後，系統將會記錄您的會員資訊。
                <br />
                未來使用同一個瀏覽器點餐，將會自動帶入聯絡人資訊。
              </Text>

              <FormControl isRequired isInvalid={!!error.name}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  姓名
                </FormLabel>
                <Input
                  variant={InputVariant.OUTLINE_DARK}
                  value={form.name}
                  onChange={(e) => handleChangeState.changeName(e.target.value)}
                  type="text"
                  inputMode="text"
                  isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
                />
                <FormErrorMessage bgColor="danger.200">{error.name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!error.phoneNumber}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  手機
                </FormLabel>
                <InputGroup>
                  <Input
                    variant={InputVariant.OUTLINE_DARK}
                    value={form.phoneNumber}
                    onChange={(e) => handleChangeState.changePhoneNumber(e.target.value)}
                    type="tel"
                    inputMode="tel"
                    isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
                  />

                  <InputRightAddon
                    as="button"
                    cursor="pointer"
                    minW="7rem"
                    justifyContent="center"
                    bgColor={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? 'gray.200' : 'dark'}
                    onClick={
                      fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? () => {} : handleLogin
                    }
                  >
                    {fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? (
                      <Spinner />
                    ) : (
                      <Text color="white">傳遞驗證碼</Text>
                    )}
                  </InputRightAddon>
                </InputGroup>
                <FormErrorMessage bgColor="danger.200">{error.phoneNumber}</FormErrorMessage>
              </FormControl>

              {fp.eq(confirmStatus, ConfirmStatus.CONFIRMED) && (
                <FormControl isRequired isInvalid={!!error.verifyCode}>
                  <FormLabel fontSize="sm" fontWeight="normal">
                    驗證碼
                  </FormLabel>
                  <InputGroup>
                    <Input
                      variant={InputVariant.OUTLINE_DARK}
                      value={form.verifyCode}
                      onChange={(e) => handleChangeState.changeVerifyCode(e.target.value)}
                      type="number"
                      inputMode="numeric"
                      isDisabled={isVerifying}
                    />
                    <InputRightElement>{isVerifying && <Spinner />}</InputRightElement>
                  </InputGroup>
                  <FormErrorMessage bgColor="danger.200">{error.verifyCode}</FormErrorMessage>
                </FormControl>
              )}
            </VStack>
            {process.env.REACT_APP_LINE_CLIENT_ID?.length ? (
              <VStack mt="7" spacing={7}>
                <HStack spacing="29px" width="100%">
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                  <Text>或</Text>
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                </HStack>
                <Button bgColor="#01B901" _hover={{ bgColor: '#01B901' }} onClick={handleLineLogin}>
                  使用LINE帳號登入
                </Button>
              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader my={6} p={0}>
          <HStack justifyContent="center" borderBottom="1px" borderColor="primary" py={2}>
            <Icon as={IoLogInOutline} boxSize="1.5rem" color="primary" />
            <Text fontSize="lg" fontWeight="normal">
              登入
            </Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="xs">
              手機登入後，系統將會記錄您的會員資訊。
              <br />
              未來使用同一個瀏覽器點餐，將會自動帶入聯絡人資訊。
            </Text>

            <FormControl isRequired isInvalid={!!error.name}>
              <FormLabel fontSize="sm" fontWeight="normal">
                姓名
              </FormLabel>
              <Input
                variant={InputVariant.OUTLINE_DARK}
                value={form.name}
                onChange={(e) => handleChangeState.changeName(e.target.value)}
                type="text"
                inputMode="text"
                isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
              />
              <FormErrorMessage bgColor="danger.200">{error.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!error.phoneNumber}>
              <FormLabel fontSize="sm" fontWeight="normal">
                手機
              </FormLabel>
              <InputGroup>
                <Input
                  variant={InputVariant.OUTLINE_DARK}
                  value={form.phoneNumber}
                  onChange={(e) => handleChangeState.changePhoneNumber(e.target.value)}
                  type="tel"
                  inputMode="tel"
                  isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
                />

                <InputRightAddon
                  as="button"
                  cursor="pointer"
                  minW="7rem"
                  justifyContent="center"
                  bgColor={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? 'gray.200' : 'dark'}
                  onClick={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? () => {} : handleLogin}
                >
                  {fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? (
                    <Spinner />
                  ) : (
                    <Text color="white">傳遞驗證碼</Text>
                  )}
                </InputRightAddon>
              </InputGroup>
              <FormErrorMessage bgColor="danger.200">{error.phoneNumber}</FormErrorMessage>
            </FormControl>

            {fp.eq(confirmStatus, ConfirmStatus.CONFIRMED) && (
              <FormControl isRequired isInvalid={!!error.verifyCode}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  驗證碼
                </FormLabel>
                <InputGroup>
                  <Input
                    variant={InputVariant.OUTLINE_DARK}
                    value={form.verifyCode}
                    onChange={(e) => handleChangeState.changeVerifyCode(e.target.value)}
                    type="number"
                    inputMode="numeric"
                    isDisabled={isVerifying}
                  />
                  <InputRightElement>{isVerifying && <Spinner />}</InputRightElement>
                </InputGroup>
                <FormErrorMessage bgColor="danger.200">{error.verifyCode}</FormErrorMessage>
              </FormControl>
            )}

            {process.env.REACT_APP_LINE_CLIENT_ID?.length ? (
              <VStack mt="7" spacing={7}>
                <HStack spacing="29px" width="100%">
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                  <Text>或</Text>
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                </HStack>
                <Button bgColor="#01B901" _hover={{ bgColor: '#01B901' }} onClick={handleLineLogin}>
                  使用LINE帳號登入
                </Button>
              </VStack>
            ) : null}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
